import api from '@/lib/api';
import { generateFields } from '@/api/generators-api';
import type { FieldDefinition } from '@/types/fields-api.model';
import type { TournamentRound, RoundScheduleSlot } from '@/types/tournament-round.model';

/**
 * Fetches every round of a LONG tournament.
 * GET /tournaments/{tournamentId}/rounds
 * @param fields - Optional field projection; without it the full summary is returned.
 */
export const getRounds = async (
  tournamentId: string,
  fields?: FieldDefinition,
): Promise<TournamentRound[]> => {
  const r = await api.get(`/tournaments/${tournamentId}/rounds`, {
    params: fields ? { fields: generateFields(fields) } : undefined,
  });
  return r.data;
};

/**
 * Fetches a specific round.
 * GET /tournaments/{tournamentId}/rounds/{roundId}
 */
export const getRound = async (tournamentId: string, roundId: string): Promise<TournamentRound> => {
  const r = await api.get(`/tournaments/${tournamentId}/rounds/${roundId}`);
  return r.data;
};

/** timeRange item inside an AvailabilityDTO. */
interface AvailabilityTimeRange {
  qty: number;
  courtsQuantity: number;
  startTime: string;
  endTime: string;
}

/** Shape of the body the backend expects (AvailabilityDTO). */
interface AvailabilityDTO {
  date: string;
  dayOfWeek: string;
  timeRange: AvailabilityTimeRange[];
}

/**
 * Converts RoundScheduleSlot[] (one per slot, the frontend's flat format) into the
 * AvailabilityDTO[] format the backend expects: groups by date/dayOfWeek and each
 * slot becomes a timeRange item.
 */
export const mapSlotsToAvailabilityDTO = (
  slots: RoundScheduleSlot[],
): AvailabilityDTO[] => {
  const byDate = new Map<string, AvailabilityDTO>();
  for (const slot of slots) {
    const key = slot.date;
    let entry = byDate.get(key);
    if (!entry) {
      entry = { date: slot.date, dayOfWeek: slot.dayOfWeek, timeRange: [] };
      byDate.set(key, entry);
    }
    entry.timeRange.push({
      qty: slot.qty,
      courtsQuantity: slot.courtsQuantity,
      startTime: slot.startTime,
      endTime: slot.endTime,
    });
  }
  return Array.from(byDate.values());
};

/**
 * Sets the schedule of a round.
 * PUT /tournaments/{tournamentId}/rounds/{roundId}/schedule
 * Body: { slots: AvailabilityDTO[] }
 *
 * The RoundScheduleSlot[] -> AvailabilityDTO[] transformation happens here (in the API layer)
 * so the UI doesn't need to know the shape of the backend body.
 */
export const setRoundSchedule = async (
  tournamentId: string,
  roundId: string,
  slots: RoundScheduleSlot[],
): Promise<TournamentRound> => {
  const body = { slots: mapSlotsToAvailabilityDTO(slots) };
  const r = await api.put(`/tournaments/${tournamentId}/rounds/${roundId}/schedule`, body);
  return r.data;
};

/**
 * Opens the round for availability submission.
 * POST /tournaments/{tournamentId}/rounds/{roundId}/open
 */
export const openRound = async (tournamentId: string, roundId: string): Promise<TournamentRound> => {
  const r = await api.post(`/tournaments/${tournamentId}/rounds/${roundId}/open`);
  return r.data;
};

/**
 * Generates the round's match proposal.
 * POST /tournaments/{tournamentId}/rounds/{roundId}/propose
 */
export const proposeRound = async (tournamentId: string, roundId: string): Promise<TournamentRound> => {
  const r = await api.post(`/tournaments/${tournamentId}/rounds/${roundId}/propose`);
  return r.data;
};

/**
 * Publishes the round's match proposal.
 * POST /tournaments/{tournamentId}/rounds/{roundId}/publish
 */
export const publishRound = async (tournamentId: string, roundId: string): Promise<TournamentRound> => {
  const r = await api.post(`/tournaments/${tournamentId}/rounds/${roundId}/publish`);
  return r.data;
};

/**
 * Reschedules a match to another round or date/time.
 * PATCH /tournaments/{tournamentId}/matches/{matchId}/reschedule
 * Body: { targetRoundId: UUID, startTime: 'yyyy-MM-ddTHH:mm:ss' }
 *
 * targetRoundId is required by the backend, even to reschedule within the same round, and
 * startTime has to fall within that round's week.
 *
 * startTime is a backend LocalDateTime: WITHOUT a zone suffix ('Z' / offset).
 * The caller must format it as 'yyyy-MM-ddTHH:mm:ss'.
 */
export const rescheduleMatch = async (
  tournamentId: string,
  matchId: string,
  payload: { targetRoundId: string; startTime: string },
): Promise<void> => {
  await api.patch(`/tournaments/${tournamentId}/matches/${matchId}/reschedule`, payload);
};
